<template>
  <div class="referral-program-drilldown-modal">
    <div class="category-top-bar sticky-header">
      <div class="top-bar">
        <p>
          <a class="btn btn-orange btn-icon back" :title="$t('general.back')" href="#" @click.prevent="backButton">
            <i class="ti-arrow-left"></i>
            {{ $t('general.back') }}
          </a>
        </p>
      </div>
    </div>

    <div class="sales-report mar-to-15">
      <div class="col-md-12 mar-bt-15">
        <p class="text-muted">
          <span>{{ $t('reports.referralProgramReport') }}</span>
          {{ $t('general.breadcrumbSeparator') }}
          <span>{{ ambassadorTitle }}</span>
          <template v-if="level === 'orders'">
            {{ $t('general.breadcrumbSeparator') }}{{ refereeTitle }}
          </template>
        </p>
      </div>

      <div v-if="level === 'referees'" class="col-md-12">
        <div class="card">
          <div class="card-content row">
            <data-tables-server
              class="campaign-table width-table referral-program-report__table"
              @query-change="onRefereesPage"
              v-loading="loading"
              :data="refereesRows"
              :total="refereesTotal"
              :pagination-props="paginationProps"
              :table-props="{ border: false }"
              style="width: 100%"
              @row-click="onRefereeRow"
            >
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="70" prop="referee_user_id" :label="$t('referralProgram.refereeId')">
                <template slot-scope="p">
                  <span class="referral-report-table__ellipsis">{{ p.row.referee_user_id || p.row.user_id || p.row.id }}</span>
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="160" :label="$t('reports.name')">
                <template slot-scope="p">
                  <span class="referral-report-table__ellipsis">{{ displayPersonName(p.row, 'referee') }}</span>
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="120" :label="$t('referralProgram.totalCashbackPercent')">
                <template slot-scope="p">
                  {{ formatMoney(refereeEarningsCashback(p.row)) }}
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="120" :label="$t('referralProgram.ambassadorEarnings')">
                <template slot-scope="p">
                  {{ formatMoney(refereeEarningsTotal(p.row)) }}
                  <small v-if="p.row.earnings && p.row.earnings.coupons_awarded" class="d-block">{{ $t('referralProgram.v2.couponsAwarded') }}: {{ p.row.earnings.coupons_awarded }}</small>
                </template>
              </el-table-column>
            </data-tables-server>
          </div>
        </div>
      </div>

      <div v-if="level === 'orders'" class="col-md-12">
        <div class="card">
          <div class="card-content row">
            <data-tables-server
              class="campaign-table width-table referral-program-report__table"
              @query-change="onOrdersPage"
              v-loading="loading"
              :data="ordersRows"
              :total="ordersTotal"
              :pagination-props="paginationProps"
              :table-props="{ border: false }"
              style="width: 100%"
            >
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="90" prop="order_id" :label="$t('general.orderId')">
                <template slot-scope="p">
                  <span class="referral-report-table__ellipsis">{{ p.row.order_id }}</span>
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="120" :label="$t('orders.totals_grandtotal')">
                <template slot-scope="p">
                  {{ formatMoney(orderGrandtotal(p.row)) }}
                </template>
              </el-table-column>
              <el-table-column v-for="role in rewardRoles" v-if="hasV2Rewards" :key="role" class-name="centered-cell" align="center" header-align="center" :min-width="190" :label="$t('referralProgram.v2.' + role)">
                <template slot-scope="p">
                  <div v-for="(reward, index) in roleRewards(p.row, role)" :key="index">{{ reward }}</div>
                  <span v-if="!roleRewards(p.row, role).length">{{ $t('general.emDash') }}</span>
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="120" v-if="!hasV2Rewards" :label="$t('referralProgram.refereeReward')">
                <template slot-scope="p">
                  {{ formatMoney(orderRefereeReward(p.row)) }}
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="120" v-if="!hasV2Rewards" :label="$t('referralProgram.ambassadorOrderReward')">
                <template slot-scope="p">
                  {{ formatMoney(orderAmbassadorOrderReward(p.row)) }}
                </template>
              </el-table-column>
              <el-table-column class-name="centered-cell" align="center" header-align="center" :min-width="120" v-if="!hasV2Rewards" :label="$t('referralProgram.ambassadorCashback')">
                <template slot-scope="p">
                  {{ formatMoney(orderAmbassadorCashback(p.row)) }}
                </template>
              </el-table-column>
            </data-tables-server>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue'
import DataTables from 'vue-data-tables'
import swal from 'sweetalert2'
import { rewardRoles, recordedRoleRewards } from '../../../Marketing/Referral/referralRewardSettings'

Vue.use(DataTables)

export default {
  props: {
    route: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      loading: false,
      rewardRoles,
      level: 'referees',
      paginationProps: { layout: 'prev, pager, next, sizes, total' },
      refereesRows: [],
      refereesTotal: 0,
      ordersRows: [],
      ordersTotal: 0,
      selectedAmbassadorId: null,
      selectedRefereeId: null,
      refereeTitle: '',
      ambassadorNameFromApi: '',
      skipRefereesQueryChange: true,
      skipOrdersQueryChange: false
    }
  },
  computed: {
    hasV2Rewards () {
      return this.ordersRows.some(row => Array.isArray(row.reward_details))
    },
    ambassadorTitle () {
      const q = this.route.query || {}
      if (q.title) return q.title
      if (this.ambassadorNameFromApi) return this.ambassadorNameFromApi
      const id = this.selectedAmbassadorId
      return id != null ? this.$t('general.idHash', { id }) : ''
    }
  },
  created () {
    const raw = this.route.params && this.route.params.ambassadorId
    this.selectedAmbassadorId = raw != null && raw !== '' ? raw : null
    if (this.selectedAmbassadorId == null) {
      swal('', this.$t('validations.wentWrong'), 'error').catch(() => {})
      return
    }
    this.fetchReferees(1, 10)
  },
  methods: {
    roleRewards (row, role) {
      const rewards = recordedRoleRewards(row, role, this.$t.bind(this))
      if (rewards !== null) return rewards
      // Historical rows have only one ambassador, without a stored level.
      if (role === 'ambassador_1') return []
      const value = role === 'referee' ? this.orderRefereeReward(row) : this.orderAmbassadorOrderReward(row)
      const legacy = []
      if (value != null) legacy.push(this.$t('referralProgram.v2.order_reward') + ': ' + this.formatMoney(value))
      const cashback = role === 'ambassador_2' ? this.orderAmbassadorCashback(row) : null
      if (cashback != null) legacy.push(this.$t('referralProgram.v2.cashback') + ': ' + this.formatMoney(cashback))
      return legacy
    },
    backButton () {
      if (this.level === 'orders') {
        this.level = 'referees'
        this.selectedRefereeId = null
        this.skipRefereesQueryChange = true
        this.fetchReferees(1, 10)
        return
      }
      this.$bus.$emit('close-modal')
    },
    formatMoney (v) {
      if (v === null || v === undefined || v === '') return this.$t('general.emDash')
      const n = Number(v)
      return isNaN(n) ? String(v) : n.toFixed(2)
    },
    pickRowAmount (row, keys) {
      if (!row || !keys) return undefined
      for (const key of keys) {
        const v = row[key]
        if (v !== null && v !== undefined && v !== '') return v
      }
      return undefined
    },
    orderGrandtotal (row) {
      if (row.order_grandtotal != null) return row.order_grandtotal
      if (row.order && row.order.totals_grandtotal != null) return row.order.totals_grandtotal
      return this.pickRowAmount(row, ['totals_grandtotal', 'order_total'])
    },
    orderRefereeReward (row) {
      if (row.referee_reward != null) return row.referee_reward
      return this.pickRowAmount(row, ['referee_loyalty_awarded', 'referee_reward_amount', 'referee_loyalty_points'])
    },
    orderAmbassadorOrderReward (row) {
      if (row.ambassador_earnings && row.ambassador_earnings.from_order_rewards != null) {
        return row.ambassador_earnings.from_order_rewards
      }
      return this.pickRowAmount(row, ['ambassador_order_reward', 'ambassador_order_reward_amount'])
    },
    orderAmbassadorCashback (row) {
      if (row.ambassador_earnings && row.ambassador_earnings.from_cashback != null) {
        return row.ambassador_earnings.from_cashback
      }
      return this.pickRowAmount(row, ['ambassador_cashback_amount'])
    },
    refereeEarningsCashback (row) {
      if (row.earnings && row.earnings.from_cashback != null) return row.earnings.from_cashback
      return row.earned_cashback_percent_total
    },
    refereeEarningsTotal (row) {
      if (row.earnings && row.earnings.total != null) return row.earnings.total
      return row.earned_for_ambassador_total || row.ambassador_earnings || row.total_ambassador_from_referee
    },
    displayPersonName (row, role) {
      if (!row) return this.$t('general.emDash')
      if (row.full_name) return row.full_name
      if (row.name) return row.name
      const fn = String(row.firstname || row.first_name || '').trim()
      const ln = String(row.lastname || row.last_name || '').trim()
      const s = [fn, ln].filter(Boolean).join(' ')
      if (s) return s
      const id = role === 'referee'
        ? (row.referee_user_id || row.user_id || row.id)
        : (row.ambassador_user_id || row.user_id || row.id)
      return this.$t('general.idHash', { id })
    },
    refereeIdFromRow (row) {
      return row.referee_user_id || row.user_id || row.id
    },
    onRefereeRow (row) {
      this.selectedRefereeId = this.refereeIdFromRow(row)
      this.refereeTitle = this.displayPersonName(row, 'referee')
      this.level = 'orders'
      this.skipOrdersQueryChange = true
      this.fetchOrders(1, 10)
    },
    normalizeRows (response, listKey) {
      if (!response || !response.data) return []
      const d = response.data
      if (Array.isArray(d)) return d
      if (listKey && Array.isArray(d[listKey])) return d[listKey]
      if (Array.isArray(d.referees)) return d.referees
      if (Array.isArray(d.orders)) return d.orders
      if (Array.isArray(d.data)) return d.data
      return []
    },
    normalizeTotal (response, listKey) {
      if (response.pagination && response.pagination.total != null) return response.pagination.total
      if (response.meta && response.meta.total != null) return response.meta.total
      const d = response.data
      if (d && d.total != null) return d.total
      return this.normalizeRows(response, listKey).length
    },
    baseParams () {
      return {
        token: this.$session.get('token'),
        company_id: this.$session.get('selectedCompany')
      }
    },
    onRefereesPage (q) {
      if (this.skipRefereesQueryChange) {
        this.skipRefereesQueryChange = false
        return
      }
      this.fetchReferees(q.page, q.pageSize)
    },
    fetchReferees (page, perPage) {
      if (!this.selectedAmbassadorId) return
      this.loading = true
      const data = Object.assign({}, this.baseParams(), { page, perPage })
      const path = '/reports/v2/referral-ambassadors/' + this.selectedAmbassadorId + '/referees?page=' + page + '&perPage=' + perPage
      this.apiRequest(path, 'GET', data, (response) => {
        if (response.status) {
          const amb = response.data && response.data.ambassador
          if (amb && amb.name) {
            this.ambassadorNameFromApi = String(amb.name).trim()
          }
          this.refereesRows = this.normalizeRows(response, 'referees')
          this.refereesTotal = this.normalizeTotal(response, 'referees')
        } else {
          swal('', this.$t('validations.wentWrong'), 'error')
        }
        this.loading = false
      })
    },
    onOrdersPage (q) {
      if (this.skipOrdersQueryChange) {
        this.skipOrdersQueryChange = false
        return
      }
      this.fetchOrders(q.page, q.pageSize)
    },
    fetchOrders (page, perPage) {
      if (!this.selectedRefereeId) return
      this.loading = true
      const data = Object.assign({}, this.baseParams(), { page, perPage })
      const path = '/reports/v2/referral-referees/' + this.selectedRefereeId + '/orders?page=' + page + '&perPage=' + perPage
      this.apiRequest(path, 'GET', data, (response) => {
        if (response.status) {
          const referee = response.data && response.data.referee
          if (referee && referee.name) {
            this.refereeTitle = referee.name
          }
          this.ordersRows = this.normalizeRows(response, 'orders')
          this.ordersTotal = this.normalizeTotal(response, 'orders')
        } else {
          swal('', this.$t('validations.wentWrong'), 'error')
        }
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.referral-program-drilldown-modal >>> div.campaign-table div.pagination-bar {
  display: block !important;
}

.referral-program-drilldown-modal >>> .el-table .cell {
  overflow: hidden;
}

.referral-program-drilldown-modal >>> .el-table .cell > .referral-report-table__ellipsis {
  min-width: 0;
}
</style>

<style>
.referral-program-drilldown-modal .centered-cell .cell {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  text-align: center !important;
}

.referral-program-drilldown-modal .centered-cell .cell .referral-report-table__ellipsis {
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: middle;
}
</style>



// WEBPACK FOOTER //
// src/components/Dashboard/Views/Reports/Versions/2/ReferralProgramAmbassadorDrilldownModal.vue